import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePoultryLoss } from "./poultry/usePoultryLoss";
import { toast } from "sonner";

export const usePoultryTransactions = () => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isTransactionDialogOpen, setIsTransactionDialogOpen] = useState(false);
  const [isReturnDialogOpen, setIsReturnDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [transactionType, setTransactionType] = useState<'purchase' | 'sale'>('purchase');

  const { data: stockItems, refetch } = useQuery({
    queryKey: ["stockItems"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stock_items')
        .select('*')
        .gt('quantity', 0);

      if (error) throw error;
      return data;
    },
  });

  const {
    isLossDialogOpen,
    setIsLossDialogOpen,
    handleLossClick,
    handleLossConfirm
  } = usePoultryLoss(refetch);

  const handleAddProduct = async (product: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Vous devez être connecté");
        return;
      }

      const { data: ownerData } = await supabase
        .from('owner_data')
        .select('business_type_id')
        .eq('owner_id', user.id)
        .single();

      if (!ownerData) {
        toast.error("Données propriétaire non trouvées");
        return;
      }

      const { data: businessStock } = await supabase
        .from('business_stocks')
        .select('id')
        .eq('business_id', ownerData.business_type_id)
        .single();

      if (!businessStock) {
        toast.error("Stock non trouvé");
        return;
      }

      const { error: insertError } = await supabase
        .from('stock_items')
        .insert({
          stock_id: businessStock.id,
          data: product.data || {
            name: product.name,
            unit: product.unit,
            price: product.price,
            minQuantity: product.minQuantity,
            status: product.status
          },
          quantity: product.quantity
        });

      if (insertError) throw insertError;

      toast.success("Volailles ajoutées avec succès");
      setIsAddDialogOpen(false);
      refetch();
    } catch (error: any) {
      console.error("Erreur lors de l'ajout des volailles:", error);
      toast.error(error.message || "Erreur lors de l'ajout des volailles");
    }
  };

  const handleReturnClick = (item: any) => {
    console.log("Return click - Setting selected item:", item);
    setSelectedItem(item);
    setIsReturnDialogOpen(true);
  };

  const handleReturnConfirm = async (quantity: number, refundAmount: number, paymentMethod: string, condition: string) => {
    if (!selectedItem) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const unitPrice = typeof selectedItem.data === 'object' && selectedItem.data 
        ? cleanNumericValue(selectedItem.data.price || '0')
        : 0;

      const { error } = await supabase
        .from('poultry_transactions')
        .insert({
          stock_item_id: selectedItem.id,
          transaction_type: 'return',
          quantity: quantity,
          unit_price: unitPrice,
          total_amount: refundAmount,
          transaction_date: new Date().toISOString(),
          created_by: user.id,
          refund_amount: refundAmount,
          refund_payment_method: paymentMethod,
          return_condition: condition
        });

      if (error) throw error;

      // Update quantity by adding instead of subtracting
      const { error: updateError } = await supabase
        .from('stock_items')
        .update({ quantity: selectedItem.quantity + quantity })
        .eq('id', selectedItem.id);

      if (updateError) throw updateError;

      toast.success("Retour enregistré avec succès");
      setIsReturnDialogOpen(false);
      setSelectedItem(null);
      refetch();
    } catch (error: any) {
      console.error("Erreur lors de l'enregistrement du retour:", error);
      toast.error(error.message || "Erreur lors de l'enregistrement du retour");
    }
  };

  // Ajout des gestionnaires de fermeture des dialogues
  const handleCloseReturnDialog = () => {
    setIsReturnDialogOpen(false);
    setSelectedItem(null);
  };

  const handleCloseLossDialog = () => {
    setIsLossDialogOpen(false);
    setSelectedItem(null);
  };

  return {
    isAddDialogOpen,
    setIsAddDialogOpen,
    isTransactionDialogOpen,
    setIsTransactionDialogOpen,
    isReturnDialogOpen,
    setIsReturnDialogOpen: handleCloseReturnDialog,
    isLossDialogOpen,
    setIsLossDialogOpen: handleCloseLossDialog,
    selectedItem,
    transactionType,
    stockItems,
    handleAddProduct,
    handleLossClick,
    handleReturnClick,
    handleLossConfirm,
    handleReturnConfirm
  };
};

const cleanNumericValue = (value: string | number): number => {
  if (typeof value === 'number') return value;
  return parseFloat(value.replace(/[^\d.-]/g, ''));
};